import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isNativeApp } from '../../utils/deviceDetection';
import { ChevronLeft, ChevronRight, Clock, Sunrise, Sun, CloudSun, Smartphone } from 'lucide-react';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const WEEKDAYS_FULL = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

// Tageszeit-Kategorien (Icons analog zu TimeSelector)
const TIME_OF_DAY = {
  morning:   { label: 'VM', Icon: Sunrise,  color: 'text-warning',  border: 'border-warning/40' },
  afternoon: { label: 'NM', Icon: Sun,      color: 'text-primary', border: 'border-primary/40' },
  evening:   { label: 'AB', Icon: CloudSun, color: 'text-info',   border: 'border-info/40' },
};

const getTimeOfDay = (date) => {
  const hour = new Date(date).getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
};

const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Montag als Wochenstart
  return new Date(d.setDate(diff));
};

const getWeekDays = (weekStart) => {
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(weekStart);
    day.setDate(day.getDate() + i);
    return day;
  });
};

const isSameDay = (d1, d2) => {
  return d1.getFullYear() === d2.getFullYear()
    && d1.getMonth() === d2.getMonth()
    && d1.getDate() === d2.getDate();
};

const formatTime = (date) => {
  return new Date(date).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

const SessionWeekView = ({ sessions = [], onJoinSession }) => {
  const navigate = useNavigate();
  const [weekOffset, setWeekOffset] = useState(0);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Mobile: nur Mo-Fr (5 Tage), Desktop: Mo-So (7 Tage)
  const [showWeekend, setShowWeekend] = useState(!isMobile);
  const visibleDayCount = showWeekend ? 7 : 5;

  const today = new Date();
  const currentWeekStart = useMemo(() => {
    const start = getWeekStart(today);
    start.setDate(start.getDate() + weekOffset * 7);
    return start;
  }, [weekOffset]);

  const weekDays = useMemo(() => getWeekDays(currentWeekStart), [currentWeekStart]);
  const visibleDays = useMemo(() => weekDays.slice(0, visibleDayCount), [weekDays, visibleDayCount]);

  // Sessions pro Tag gruppieren, zeitlich sortiert & nach Tageszeit gegliedert
  const sessionsByDay = useMemo(() => {
    const map = {};
    weekDays.forEach((day, idx) => {
      const daySessions = sessions
        .filter(s => s.startTime && isSameDay(new Date(s.startTime), day))
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

      // Nach Tageszeit gruppieren (Reihenfolge: morning → afternoon → evening)
      const grouped = [];
      let lastTimeOfDay = null;
      daySessions.forEach(session => {
        const tod = getTimeOfDay(session.startTime);
        if (tod !== lastTimeOfDay) {
          grouped.push({ type: 'separator', timeOfDay: tod });
          lastTimeOfDay = tod;
        }
        grouped.push({ type: 'session', session });
      });

      map[idx] = { sessions: daySessions, grouped };
    });
    return map;
  }, [sessions, weekDays]);

  // Weekend Sessions zählen (für Hinweis)
  const weekendSessionCount = useMemo(() => {
    return (sessionsByDay[5]?.sessions?.length || 0) + (sessionsByDay[6]?.sessions?.length || 0);
  }, [sessionsByDay]);

  const weekLabel = useMemo(() => {
    const start = visibleDays[0];
    const end = visibleDays[visibleDays.length - 1];
    const opts = { day: 'numeric', month: 'short' };
    return `${start.toLocaleDateString('de-DE', opts)} – ${end.toLocaleDateString('de-DE', opts)}`;
  }, [visibleDays]);

  const isCurrentWeek = weekOffset === 0;

  return (
    <div className="space-y-3">
      {/* Wochen-Navigation */}
      <div className="flex items-center justify-between px-2">
        <button
          onClick={() => setWeekOffset(w => w - 1)}
          className="p-2 rounded-xl hover:bg-foreground/5 transition-colors"
          aria-label="Vorherige Woche"
        >
          <ChevronLeft className="h-5 w-5 text-muted-foreground" />
        </button>

        <div className="text-center flex items-center gap-2">
          <span className="font-semibold text-foreground text-sm">{weekLabel}</span>
          {!isCurrentWeek && (
            <button
              onClick={() => setWeekOffset(0)}
              className="text-xs text-primary hover:opacity-80 font-medium"
            >
              Heute
            </button>
          )}
        </div>

        <button
          onClick={() => setWeekOffset(w => w + 1)}
          className="p-2 rounded-xl hover:bg-foreground/5 transition-colors"
          aria-label="Nächste Woche"
        >
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </button>
      </div>

      {/* Wochen-Grid - 5 oder 7 Spalten, kompakt auf Mobile */}
      <div className={`grid gap-px sm:gap-1.5 ${showWeekend ? 'grid-cols-7' : 'grid-cols-5'}`}>
        {visibleDays.map((day, idx) => {
          const isToday = isSameDay(day, today);
          const dayData = sessionsByDay[idx] || { sessions: [], grouped: [] };
          const isPast = day < today && !isToday;
          // Nur Separator anzeigen, wenn es mehr als eine Tageszeit gibt
          const distinctTimes = new Set(dayData.sessions.map(s => getTimeOfDay(s.startTime)));
          const showSeparators = distinctTimes.size > 1;

          return (
            <div
              key={idx}
              className={`rounded-lg sm:rounded-xl p-1 sm:p-2.5 min-h-[100px] sm:min-h-[120px] min-w-0 overflow-hidden transition-colors duration-fast ${
                isToday
                  ? 'bg-primary-soft border-2 border-primary/50 shadow-token-sm'
                  : isPast
                    ? 'bg-surface-raised/40 border border-border'
                    : 'bg-surface/40 border border-border'
              }`}
            >
              {/* Tag-Header – kompakt auf Mobile */}
              <div className="text-center mb-1 sm:mb-2">
                <div className={`text-2xs sm:text-xs font-medium ${
                  isToday ? 'text-primary' : 'text-muted-foreground'
                }`}>
                  {WEEKDAYS[idx]}
                </div>
                <div className={`text-sm sm:text-base font-bold leading-tight ${
                  isToday
                    ? 'text-primary-foreground bg-primary rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center mx-auto mt-0.5 text-xs sm:text-base'
                    : isPast ? 'text-faint' : 'text-foreground'
                }`}>
                  {day.getDate()}
                </div>
              </div>

              {/* Sessions des Tages – alle, zeitlich sortiert, nach Tageszeit gruppiert */}
              <div className="space-y-0.5 sm:space-y-1">
                {dayData.grouped.map((item, i) => {
                  if (item.type === 'separator') {
                    const tod = TIME_OF_DAY[item.timeOfDay];
                    const Icon = tod.Icon;
                    if (!showSeparators) return null;
                    return (
                      <div key={`sep-${item.timeOfDay}`} className="flex items-center gap-0.5 py-px sm:py-0.5">
                        <Icon className={`h-3 w-3 flex-shrink-0 ${tod.color}`} />
                        <div className={`flex-1 h-px ${tod.border} border-t border-dashed`} />
                      </div>
                    );
                  }

                  const session = item.session;
                  return (
                    <button
                      key={session.id}
                      onClick={() => navigate(`/session/${session.id}`)}
                      className={`w-full text-left rounded p-1 sm:rounded-lg sm:p-1.5 text-2xs sm:text-xs min-w-0 overflow-hidden transition-transform duration-fast active:scale-95 sm:hover:scale-[1.02] ${
                        session.status === 'ACTIVE'
                          ? 'bg-success-soft border border-success/40 text-foreground'
                          : 'bg-info-soft border border-info/30 text-foreground'
                      }`}
                      title={`${session.topic} – ${WEEKDAYS_FULL[idx]} ${formatTime(session.startTime)}`}
                    >
                      <div className="font-medium leading-tight line-clamp-2 break-words">{session.topic}</div>
                      {/* Mindestens 11 px (Okt 2026); auf dem Telefon reicht die
                          Zellbreite dann nur fuer die Uhrzeit, ohne Uhr-Icon. */}
                      <div className="flex items-center gap-0.5 text-2xs opacity-75 mt-px sm:mt-0.5">
                        <Clock className="hidden sm:block h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{formatTime(session.startTime)}</span>
                      </div>
                    </button>
                  );
                })}

                {dayData.sessions.length === 0 && !isPast && (
                  <div className="text-center py-1 sm:py-2">
                    <div className="text-2xs sm:text-xs text-faint">–</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Landscape-Hinweis – nur im Handy-Browser. Die native App ist seit
          Oktober 2026 auf Hochformat gesperrt; dort verlangte der Hinweis etwas,
          das gar nicht mehr geht. */}
      {!isNativeApp() && (
        <div className="flex sm:hidden items-center justify-center gap-1.5 text-2xs text-faint px-2">
          <Smartphone className="h-3 w-3 rotate-90" />
          <span>Querformat für bessere Übersicht</span>
        </div>
      )}

      {/* Footer: Legende + Wochenende Toggle */}
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-3 text-2xs sm:text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded bg-success-soft border border-success/50" />
            <span>Aktiv</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded bg-info-soft border border-info/50" />
            <span>Geplant</span>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 ml-1 text-faint">
            <span>|</span>
            <Sunrise className="h-3 w-3 text-warning" />
            <span>VM</span>
            <Sun className="h-3 w-3 text-primary" />
            <span>NM</span>
            <CloudSun className="h-3 w-3 text-info" />
            <span>AB</span>
          </div>
        </div>

        {/* Sa/So Toggle */}
        <button
          onClick={() => setShowWeekend(prev => !prev)}
          className={`text-2xs sm:text-xs font-medium px-2 py-1 rounded-lg transition-colors duration-fast ${
            showWeekend
              ? 'text-primary bg-primary-soft'
              : 'text-muted-foreground hover:text-foreground hover:bg-foreground/5'
          }`}
        >
          {showWeekend ? 'Mo–Fr' : `Sa/So${weekendSessionCount > 0 ? ` (${weekendSessionCount})` : ''}`}
        </button>
      </div>
    </div>
  );
};

export default SessionWeekView;
